/* Copyright 2020 Enrico Ros. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/

// CONFIGURATION
const USE_SHADOWS = true;
const parentColors = {
  background: '#b5007d',
  softText: 'rgb(249, 226, 236)',
  darkThemeBackground: '#222222',
  darkThemeText: '#FFF',
  darkButtonColor: '#c67519',
}
const defaultFonts = [
  '"Roboto"',       // was the previous dominant
  '"Helvetica"',    // fall-backs ...
  '"Arial"',
  'sans-serif',
]
// applied in index.js with createMuiTheme -> to theme Mui components
// ALSO see: material-kit-react.js for 'primaryColor', etc..
const muiThemePalette = {
  primary: {main: parentColors.background}, // NOT primaryColor: #9c27b0 (more violet)
  secondary: {main: '#e91e63'},       // mkr:roseColor
  error: {main: '#f44336'},           // mkr:dangerColor
  warning: {main: '#ff9800'},         // mkr:warningColor
  info: {main: '#00acc1'},            // mkr:infoColor
  success: {main: '#4caf50'},         // mkr:successColor
  // background: {default: '#fff',},
};

const barStyle = (backgroundColorHex) => {
  const coloredBarShadow = (colorHexString) => `0 4px 20px 0px #00000024, 0 7px 12px -5px ${colorHexString}75`;
  return {
    color: '#FFF',
    backgroundColor: backgroundColorHex,
    boxShadow: USE_SHADOWS ? coloredBarShadow(backgroundColorHex) : 'none',
  };
};


// Brand collection
export const Brand = {
  // brand values for this application
  appName: 'Bro Down!',
  // appDefaultTitle: 'Bro Down! - A Soulreplica experiment',
  // appShortTagline: 'Experiment',
  appBlogLink: 'https://www.soulreplica.com/news/hello-brodown/',

  // main strings for the app
  rootPageSecondary: 'Welcome',
  chatsListTitle: 'Chat Rooms',

  // parent brand (SR)
  parentBrandName: 'Soulreplica',
  parentBrandWebsite: 'https://www.soulreplica.com',

  // colors: mapping for the MUI components
  muiThemePalette: muiThemePalette,
  muiThemeTypography: {
    fontFamily: ['"Inter"', ...defaultFonts,].join(', '),
  },
  typographySerif: {
    fontFamily: ['"Crimson Text"', ...defaultFonts,].join(', '),
  },
  // colors:
  primaryColor: parentColors.background,
  invertedButtonColor: parentColors.darkButtonColor,
  // styles
  styles: {
    // app bar (also other raised colored glass surfaces)
    appbar_brand: barStyle('#9c27b0'),
    appbar_rose: barStyle(muiThemePalette.secondary.main),
    appbar_red: barStyle(muiThemePalette.error.main),
    appbar_orange: barStyle(muiThemePalette.warning.main),
    appbar_blue: barStyle(muiThemePalette.info.main),
    appbar_green: barStyle(muiThemePalette.success.main),
    // special themes - this when announcer: chef
    appbar_ann_chef: barStyle(muiThemePalette.error.main), // #3f261d AMC Mocha Brown, #6a3605, e91e63
    // explainers
    inverted: {
      ...barStyle(parentColors.darkThemeBackground),
      color: parentColors.darkThemeText,
    },
    // footer
    footer_brand: {
      color: parentColors.softText,
      backgroundColor: parentColors.background,
      boxShadow: USE_SHADOWS ? '0 -3px 10px 0px #00000024' : 'none',
    },
  },
}

// keep this in sync with stage-enums.ts
const chatThemeToBrandStyle = {
  'default': 'appbar_brand',
  'rose': 'appbar_rose',
  'red': 'appbar_red',
  'orange': 'appbar_orange',
  'blue': 'appbar_blue',
  'green': 'appbar_green',
  'chef': 'appbar_ann_chef',
};

export function brandStyleKey(theme: string | null): string {
  if (theme in chatThemeToBrandStyle)
    return chatThemeToBrandStyle[theme];
  theme && console.warn(`brandStyleKey: invalid snapshot/theme ${theme}`);
  return chatThemeToBrandStyle['default'];
}
